import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface MovieCardProps {
  movie: Movie;
  variant?: 'default' | 'compact' | 'featured';
}

export default function MovieCard({ movie, variant = 'default' }: MovieCardProps) {
  const isCompact = variant === 'compact';
  const isFeatured = variant === 'featured';

  if (isFeatured) {
    return (
      <Link
        to={`/article/${movie.id}`}
        className="group block relative overflow-hidden rounded-2xl cursor-pointer"
      >
        <div className="aspect-[16/9] relative overflow-hidden rounded-2xl">
          <img
            src={movie.banner}
            alt={movie.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="flex items-center gap-2 mb-2">
              {movie.isNew && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-accent-500 text-background-50 whitespace-nowrap">
                  最新
                </span>
              )}
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm whitespace-nowrap">
                {movie.region}
              </span>
              <span className="text-background-50/80 text-sm">{movie.year}</span>
            </div>
            <h3 className="font-heading text-xl font-bold text-background-50 mb-2">
              {movie.title}
            </h3>
            <div className="flex items-center gap-3 text-background-50/80 text-sm">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500 text-sm"></i>
                {movie.rating}
              </span>
              <span>{movie.duration || movie.episodes}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (isCompact) {
    return (
      <Link
        to={`/article/${movie.id}`}
        className="group block cursor-pointer"
      >
        <div className="relative aspect-[3/4] overflow-hidden rounded-lg mb-2">
          <img
            src={movie.poster}
            alt={movie.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          {movie.isNew && (
            <span className="absolute top-2 left-2 px-1.5 py-0.5 text-[10px] font-medium rounded bg-accent-500 text-background-50 whitespace-nowrap">
              新
            </span>
          )}
          <div className="absolute top-2 right-2 px-1.5 py-0.5 text-[10px] font-medium rounded bg-black/60 text-background-50 whitespace-nowrap backdrop-blur-sm">
            {movie.rating}
          </div>
        </div>
        <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {movie.title}
        </h4>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="aspect-[2/3] overflow-hidden">
        <img
          src={movie.poster}
          alt={movie.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {movie.isNew && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-accent-100 text-accent-800 whitespace-nowrap">
              最新
            </span>
          )}
          <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
            {movie.tags[0]}
          </span>
        </div>
        <h3 className="font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {movie.title}
        </h3>
        <p className="text-foreground-500 text-xs mb-3 line-clamp-2">
          {movie.description}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-star-fill text-accent-500 text-xs"></i>
            {movie.rating}
          </span>
          <span>{movie.duration || movie.episodes}</span>
          <span>{movie.year}</span>
        </div>
      </div>
    </Link>
  );
}